/**
 * Pricing calculator engine.
 *
 * Ported from the standalone financing-calculator prototype. Every monetary
 * value is held as integer cents while simulating and converted to major
 * units only on output, so repayment totals reconcile to the cent.
 *
 * Conventions (see `methodology` copy on the /pricing page):
 * - Funding-months run from the funding date to its next calendar anniversary,
 *   clamped to month-end (31 Jan → 29 Feb → 31 Mar).
 * - Revenue is spread evenly across the actual days of each funding-month.
 * - Collections happen every `step` days (1 = daily, 7 = weekly). Term Loan
 *   repayments also force a final collection on the term end date.
 * - A monthly cap limits what is collected within a funding-month; unused
 *   headroom expires and shortfalls carry forward as balance, never arrears.
 * - Actuarial APR follows Reg Z Appendix J (b)(8): whole periods compound,
 *   fractional periods accrue simply. It is a comparison convention only.
 */

export const DAY_MS = 86_400_000;

/** Cash Advance forecasts stop here; an unpaid balance is not "paid off". */
export const FORECAST_HORIZON_MONTHS = 120;

export type RepaymentStructure = "term-loan" | "cash-advance";
export type RepaymentFrequency = "daily" | "weekly";

export interface SimulationParams {
  /** Funding amount in major currency units. */
  amount: number;
  /** Fixed fee as a percentage of the funding amount. */
  feePercent: number;
  /** Funding date as an ISO `YYYY-MM-DD` string. */
  startDate: string;
  structure: RepaymentStructure;
  frequency: RepaymentFrequency;
  /** Term Loan only: agreed term in months. */
  termMonths: number;
  /** Cash Advance only: share of revenue remitted, as a percentage. */
  revenueSharePercent: number;
  /** Cash Advance only: first funding-month revenue. */
  monthlyRevenue: number;
  /** Cash Advance only: month-on-month revenue growth, as a percentage. */
  monthlyGrowthPercent: number;
  /** Cash Advance only: per-month revenue overrides keyed by month index. */
  revenueOverrides: Readonly<Record<number, number>>;
  /** Cash Advance only: whether a monthly repayment cap applies. */
  capEnabled: boolean;
  /** Cash Advance only: maximum collected per funding-month. */
  monthlyCap: number;
}

export interface CollectionRow {
  /** Days since funding on which the collection happens. */
  day: number;
  /** ISO date of the collection. */
  date: string;
  /** Funding-month index the collection is attributed to. */
  month: number;
  /** Revenue accrued since the previous collection (Cash Advance). */
  revenue: number;
  payment: number;
  /** Cumulative repaid after this collection. */
  paid: number;
  /** Balance remaining after this collection. */
  balance: number;
  /** True when the monthly cap trimmed this collection. */
  capped: boolean;
}

export interface MonthSummary {
  month: number;
  start: string;
  end: string;
  /** Forecast revenue for the funding-month (0 for Term Loan). */
  revenue: number;
  payment: number;
  /** Balance remaining at the end of the funding-month. */
  balance: number;
  capHit: boolean;
}

export interface ActuarialResult {
  apr: number;
  periodRate: number;
  periodsPerYear: number;
  residual: number;
  basis: "daily" | "weekly" | "single";
  unitDays: number;
}

export interface SimulationResult {
  principal: number;
  fee: number;
  total: number;
  rows: CollectionRow[];
  months: MonthSummary[];
  /** True when the balance reaches exactly zero within the horizon. */
  complete: boolean;
  /** Days from funding to payoff, or null when incomplete. */
  days: number | null;
  /** Unpaid balance at the horizon (0 when complete). */
  balance: number;
  actuarial: ActuarialResult | null;
  /** Nominal actuarial APR as a decimal (0.16 = 16%), or null. */
  apr: number | null;
  /** Effective annual rate (cashflow IRR) as a decimal, or null. */
  effective: number | null;
  /** ISO payoff date, or null when incomplete. */
  payoff: string | null;
}

export function parseIsoDate(value: string): Date {
  return new Date(`${value}T00:00:00Z`);
}

export function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Add calendar months in UTC, clamping to the last day of the target month. */
export function addMonths(start: Date, count: number): Date {
  const result = new Date(start);
  const dayOfMonth = result.getUTCDate();
  result.setUTCDate(1);
  result.setUTCMonth(result.getUTCMonth() + count);
  const lastDay = new Date(
    Date.UTC(result.getUTCFullYear(), result.getUTCMonth() + 1, 0)
  ).getUTCDate();
  result.setUTCDate(Math.min(dayOfMonth, lastDay));
  return result;
}

const toCents = (value: number): number => Math.round(value * 100);
const fromCents = (cents: number): number => cents / 100;
const roundMoney = (value: number): number => Math.round(value * 100) / 100;

/**
 * Effective annual rate: solves the continuously compounded force that
 * discounts every dated payment back to the advance, using actual days over a
 * 365-day year, then converts to annual compounding.
 */
export function effectiveRate(
  amount: number,
  rows: readonly Pick<CollectionRow, "payment" | "day">[]
): number | null {
  const flows = rows.filter((row) => row.payment > 0);
  if (flows.length === 0) return null;
  const total = flows.reduce((sum, row) => sum + row.payment, 0);
  if (Math.abs(total - amount) < 0.005) return 0;

  const presentValueGap = (force: number) =>
    flows.reduce(
      (sum, row) => sum + row.payment * Math.exp((-force * row.day) / 365),
      0
    ) - amount;

  let low = 0;
  let high = 1;
  while (presentValueGap(high) > 0 && high < 1024) high *= 2;
  if (presentValueGap(high) > 0) return null;
  for (let i = 0; i < 90; i++) {
    const mid = (low + high) / 2;
    if (presentValueGap(mid) > 0) low = mid;
    else high = mid;
  }
  const rate = Math.expm1((low + high) / 2);
  return Number.isFinite(rate) ? rate : null;
}

export interface ActuarialCashflow {
  payment: number;
  /** Whole unit-periods since the advance. */
  whole: number;
  /** Remaining fraction of a unit-period, in [0, 1). */
  fraction: number;
}

/**
 * Reg Z Appendix J (b)(8) general equation for a single advance:
 * A = Σ Pₖ / [(1 + fₖ·i)(1 + i)^nₖ]. Bisection on the periodic rate.
 */
export function solveActuarial(
  amount: number,
  cashflows: readonly ActuarialCashflow[],
  periodsPerYear: number
): Omit<ActuarialResult, "basis" | "unitDays"> | null {
  if (
    !Number.isFinite(amount) ||
    amount <= 0 ||
    !Number.isFinite(periodsPerYear) ||
    periodsPerYear <= 0
  ) {
    throw new Error("Invalid actuarial basis");
  }
  const invalid = cashflows.some(
    (flow) =>
      !Number.isFinite(flow.payment) ||
      flow.payment < 0 ||
      !Number.isInteger(flow.whole) ||
      flow.whole < 0 ||
      !Number.isFinite(flow.fraction) ||
      flow.fraction < 0 ||
      flow.fraction >= 1 ||
      (flow.whole === 0 && flow.fraction === 0)
  );
  if (invalid) throw new Error("Invalid repayment cashflow");

  const flows = cashflows.filter((flow) => flow.payment > 0);
  const total = flows.reduce((sum, flow) => sum + flow.payment, 0);
  if (flows.length === 0 || total < amount - 0.005) return null;

  const presentValue = (rate: number) =>
    flows.reduce(
      (sum, flow) =>
        sum +
        (flow.payment * Math.exp(-flow.whole * Math.log1p(rate))) /
          (1 + flow.fraction * rate),
      0
    );

  if (Math.abs(total - amount) < 0.005) {
    return { apr: 0, periodRate: 0, periodsPerYear, residual: total - amount };
  }

  let low = 0;
  let high = 1;
  while (presentValue(high) > amount && high < 1e12) high *= 2;
  if (presentValue(high) > amount) return null;
  for (let i = 0; i < 100; i++) {
    const mid = (low + high) / 2;
    if (presentValue(mid) > amount) low = mid;
    else high = mid;
  }
  const periodRate = (low + high) / 2;
  const residual = presentValue(periodRate) - amount;
  if (
    !Number.isFinite(periodRate) ||
    Math.abs(residual) > Math.max(1e-7, amount * 1e-11)
  ) {
    return null;
  }
  return {
    apr: periodRate * periodsPerYear,
    periodRate,
    periodsPerYear,
    residual,
  };
}

/**
 * Chooses the Appendix J unit-period for a schedule: the collection cadence
 * for multi-payment schedules, or the payment's own term for a single payment
 * within 365 days. Longer isolated payments are not assigned a rate.
 */
export function actuarialRate(
  amount: number,
  rows: readonly Pick<CollectionRow, "payment" | "day">[],
  frequency: RepaymentFrequency
): ActuarialResult | null {
  const positive = rows.filter((row) => row.payment > 0);
  if (positive.length === 0) return null;

  if (positive.length === 1) {
    const [only] = positive;
    if (!only || only.day > 365) return null;
    const value = solveActuarial(
      amount,
      [{ payment: only.payment, whole: 1, fraction: 0 }],
      365 / only.day
    );
    return value ? { ...value, basis: "single", unitDays: only.day } : null;
  }

  const unitDays = frequency === "daily" ? 1 : 7;
  const periodsPerYear = frequency === "daily" ? 365 : 52;
  const value = solveActuarial(
    amount,
    rows.map((row) => ({
      payment: row.payment,
      whole: Math.floor(row.day / unitDays),
      fraction: (row.day % unitDays) / unitDays,
    })),
    periodsPerYear
  );
  return value ? { ...value, basis: frequency, unitDays } : null;
}

/** Runs the repayment simulation. `ignoreCap` produces the uncapped twin. */
export function simulate(
  params: SimulationParams,
  ignoreCap = false
): SimulationResult {
  const start = parseIsoDate(params.startDate);
  const principalCents = toCents(params.amount);
  const feeCents = toCents((params.amount * params.feePercent) / 100);
  const totalCents = principalCents + feeCents;

  const fixed = params.structure === "term-loan";
  const limit = fixed ? params.termMonths : FORECAST_HORIZON_MONTHS;
  const bounds = Array.from({ length: limit + 1 }, (_, i) =>
    addMonths(start, i)
  );
  const lastBound = bounds[limit];
  if (!lastBound) throw new Error("Invalid horizon");
  const lastDay = Math.round((lastBound.getTime() - start.getTime()) / DAY_MS);
  const step = params.frequency === "daily" ? 1 : 7;

  const count = Math.ceil(lastDay / step);
  const regular = Math.floor(totalCents / count);
  const remainder = totalCents % count;

  const rows: CollectionRow[] = [];
  const months: MonthSummary[] = [];

  let previousRevenue = params.monthlyRevenue;
  for (let i = 0; i < limit; i++) {
    let revenue = 0;
    if (!fixed) {
      if (Object.hasOwn(params.revenueOverrides, i)) {
        revenue = params.revenueOverrides[i] ?? 0;
      } else if (i === 0) {
        revenue = params.monthlyRevenue;
      } else {
        revenue = previousRevenue * (1 + params.monthlyGrowthPercent / 100);
      }
    }
    previousRevenue = revenue;
    const monthStart = bounds[i];
    const monthEnd = bounds[i + 1];
    if (!monthStart || !monthEnd) throw new Error("Invalid month bounds");
    months.push({
      month: i,
      start: toIsoDate(monthStart),
      end: toIsoDate(monthEnd),
      revenue,
      payment: 0,
      balance: fromCents(totalCents),
      capHit: false,
    });
  }

  let balance = totalCents;
  let month = 0;
  let pendingSales = 0;
  let collected = 0;
  let paidInMonth = 0;

  for (let day = 1; day <= lastDay && balance > 0; day++) {
    const earningDate = new Date(start.getTime() + (day - 1) * DAY_MS);
    while (month + 1 < limit) {
      const nextBound = bounds[month + 1];
      if (!nextBound || earningDate < nextBound) break;
      month++;
    }

    const currentMonth = months[month];
    const monthStart = bounds[month];
    const monthEnd = bounds[month + 1];
    if (!currentMonth || !monthStart || !monthEnd) break;

    if (!fixed) {
      const daysInMonth = (monthEnd.getTime() - monthStart.getTime()) / DAY_MS;
      pendingSales += currentMonth.revenue / daysInMonth;
    }

    const isCollectionDay = day % step === 0 || (fixed && day === lastDay);
    if (!isCollectionDay) continue;

    const when = new Date(start.getTime() + day * DAY_MS);
    // A funding-month is (anniversary, next anniversary], so a collection on
    // the anniversary itself belongs to the month just ending.
    const collectionMonth = month;
    const previousRow = rows.at(-1);
    if (previousRow && previousRow.month !== collectionMonth) paidInMonth = 0;

    const desired = fixed
      ? regular + (rows.length < remainder ? 1 : 0)
      : Math.max(0, toCents((pendingSales * params.revenueSharePercent) / 100));
    const headroom =
      !fixed && params.capEnabled && !ignoreCap
        ? Math.max(0, toCents(params.monthlyCap) - paidInMonth)
        : Number.POSITIVE_INFINITY;

    const payment = Math.min(balance, desired, headroom);
    balance -= payment;
    paidInMonth += payment;
    collected += payment;
    const capped = desired > headroom && balance > 0;

    currentMonth.payment += fromCents(payment);
    currentMonth.capHit ||= capped;

    rows.push({
      day,
      date: toIsoDate(when),
      month: collectionMonth,
      revenue: pendingSales,
      payment: fromCents(payment),
      paid: fromCents(collected),
      balance: fromCents(balance),
      capped,
    });
    pendingSales = 0;
  }

  let running = fromCents(totalCents);
  for (const summary of months) {
    summary.payment = roundMoney(summary.payment);
    running = roundMoney(running - summary.payment);
    summary.balance = running;
  }

  const complete = balance === 0;
  const lastRow = rows.at(-1);
  const days = complete && lastRow ? lastRow.day : null;
  const principal = fromCents(principalCents);
  const actuarial = complete
    ? actuarialRate(principal, rows, params.frequency)
    : null;

  return {
    principal,
    fee: fromCents(feeCents),
    total: fromCents(totalCents),
    rows,
    months,
    complete,
    days,
    balance: fromCents(balance),
    actuarial,
    apr: actuarial?.apr ?? null,
    effective: complete ? effectiveRate(principal, rows) : null,
    payoff: complete && lastRow ? lastRow.date : null,
  };
}

/**
 * Repaid and remaining amounts as of a given day, from completed collections
 * only. Never interpolates between collection dates.
 */
export function repaymentAtDay(
  schedule: Pick<SimulationResult, "rows" | "total">,
  day: number
): { paid: number; remaining: number } {
  let low = 0;
  let high = schedule.rows.length;
  while (low < high) {
    const mid = (low + high) >>> 1;
    const row = schedule.rows[mid];
    if (row && row.day <= day) low = mid + 1;
    else high = mid;
  }
  const paid = low > 0 ? (schedule.rows[low - 1]?.paid ?? 0) : 0;
  return {
    paid,
    remaining: roundMoney(Math.max(0, schedule.total - paid)),
  };
}

/** Average days per month used to express a day count as months. */
export const DAYS_PER_MONTH = 365.25 / 12;

/**
 * Fee expressed per month of actual repayment: the quoted fee divided by the
 * months the schedule takes. A schedule that repays faster carries a higher
 * fee per month for the same headline fee.
 */
export function feePerMonthPercent(
  feePercent: number,
  result: Pick<SimulationResult, "complete" | "days">,
  structure: RepaymentStructure,
  termMonths: number
): number | null {
  if (structure === "term-loan") {
    return termMonths > 0 ? feePercent / termMonths : null;
  }
  if (!result.complete || result.days === null || result.days <= 0) return null;
  return feePercent / (result.days / DAYS_PER_MONTH);
}

/** Months taken to repay, as a decimal (Term Loan returns the agreed term). */
export function monthsToRepay(
  result: Pick<SimulationResult, "complete" | "days">,
  structure: RepaymentStructure,
  termMonths: number
): number | null {
  if (structure === "term-loan") return termMonths;
  if (!result.complete || result.days === null) return null;
  return result.days / DAYS_PER_MONTH;
}
